/* eslint react-hooks/exhaustive-deps: "warn" */
import { type FormEvent, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { isNotBusinessEmail } from 'libs/string';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Input } from 'ui/atoms/Input/Input';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningFinalCta.module.scss';

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <path
      d="M2.6 8h10M8.4 3.8 12.6 8l-4.2 4.2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <circle cx="11" cy="11" r="10" stroke="#2e9e76" strokeWidth="1.6" />
    <path d="M6.5 11.2 9.4 14l6-6.4" stroke="#2e9e76" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type Values = { name: string; email: string; firm: string };

const EMPTY_VALUES: Values = { name: '', email: '', firm: '' };

export const InvestmentScreeningFinalCta = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);

  const [values, setValues] = useState<Values>(EMPTY_VALUES);
  const [submitted, setSubmitted] = useState(false);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    const node = rootRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const setField = (field: keyof Values) => (value: string) => setValues((prev) => ({ ...prev, [field]: value }));

  const nameError = attempted && !values.name.trim() ? t('Enter your full name') : undefined;
  const firmError = attempted && !values.firm.trim() ? t('Enter your firm') : undefined;
  const emailError =
    attempted && (!values.email.trim() || isNotBusinessEmail(values.email))
      ? t('Enter a business email address')
      : undefined;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);

    if (!values.name.trim() || !values.email.trim() || !values.firm.trim() || isNotBusinessEmail(values.email)) {
      return;
    }

    setSubmitted(true);
  };

  return (
    <Section background="#ffffff" className={styles.root}>
      <div className={styles.glow} aria-hidden="true">
        <span className={styles.blobOne} />
        <span className={styles.blobTwo} />
      </div>

      <div ref={rootRef} className={cn(styles.grid, styles.reveal)}>
        <div className={styles.copy}>
          <TitleBlock
            className={styles.titleBlock}
            align="left"
            title={t('Compare it to your current source.')}
            description={t(
              'Send a sector, a geography and a size band, or a longlist you already hold. We run it against the registry record and hand back the same names with provenance on every field, so you can put the two side by side.',
            )}
          />
        </div>

        <div className={styles.formWrap}>
          {submitted ? (
            <div className={styles.success}>
              <span className={styles.successIcon}>
                <CheckIcon />
              </span>
              <span className={styles.successTitle}>{t('Request sent.')}</span>
              <p className={styles.successText}>
                {t("We'll run the comparison against the registry record and get back to you at")} <b>{values.email}</b>
                .
              </p>
            </div>
          ) : (
            <form className={styles.form} onSubmit={onSubmit} noValidate>
              <div className={styles.field} style={{ transitionDelay: '0ms' }}>
                <Input
                  htmlType="text"
                  name="name"
                  autoComplete="name"
                  placeholder={t('Full name')}
                  ariaLabel={t('Full name')}
                  value={values.name}
                  onChange={setField('name')}
                  error={nameError}
                  className={styles.input}
                  fullWidth
                />
              </div>
              <div className={styles.field} style={{ transitionDelay: '70ms' }}>
                <Input
                  htmlType="email"
                  name="email"
                  autoComplete="email"
                  placeholder={t('Work email')}
                  ariaLabel={t('Work email')}
                  value={values.email}
                  onChange={setField('email')}
                  error={emailError}
                  className={styles.input}
                  fullWidth
                />
              </div>
              <div className={styles.field} style={{ transitionDelay: '140ms' }}>
                <Input
                  htmlType="text"
                  name="firm"
                  autoComplete="organization"
                  placeholder={t('Firm')}
                  ariaLabel={t('Firm')}
                  value={values.firm}
                  onChange={setField('firm')}
                  error={firmError}
                  className={styles.input}
                  fullWidth
                />
              </div>

              <div className={styles.field} style={{ transitionDelay: '210ms' }}>
                <Button htmlType="submit" type="blue" size="xl" width="100%" suffix={<ArrowIcon />}>
                  {t('Run the comparison')}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
};
